import { Shield, Compass, Users, MapPin, Sparkles, Handshake } from "lucide-react";
import { IMG } from "../lib/brand";
import { useContent } from "../context/ContentContext";

const governorDuties = [
    "Champion the Meraki vision and culture",
    "Strategic guidance on key decisions",
    "Guardrails — protect from negativity",
    "Authority to add or remove inactive riders",
    "Support Ambassadors during planning",
    "Maintain records of rides and activities",
];

const ambassadorDuties = [
    "Plan weekend breakfast & evening rides",
    "Execute outstation tours",
    "Coordinate collab rides with other groups",
    "Arrange ride & merch sponsorships",
    "Welcome new riders to the group",
    "Embody the Meraki spirit on the road",
];

export default function HowWeWork() {
    const { content } = useContent();
    const h = content.how;
    const texture = content.brand?.texture_gold || IMG.goldTexture;
    const showPositions = h.open_positions_enabled !== "false";

    return (
        <section id="how" data-testid="how-section" className="relative py-24 md:py-40 bg-meraki-bg">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <span className="font-mono text-meraki-gold text-sm">02</span>
                            <div className="w-8 h-px bg-meraki-gold" />
                            <span className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold">{h.eyebrow}</span>
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] text-meraki-text">
                            {h.heading_line1}<br />
                            <span className="text-meraki-gold">{h.heading_line2}</span>
                        </h2>
                    </div>
                    <p className="text-meraki-muted max-w-md leading-relaxed whitespace-pre-line">{h.intro}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* 1. Governor */}
                    <div data-testid="governor-card" className="lg:col-span-12 bg-meraki-surface border border-meraki-gold/20 p-8 md:p-12 relative overflow-hidden group">
                        <div className="absolute -top-12 -right-12 w-48 h-48 border border-meraki-gold/10 rotate-12" />
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative">
                            <div className="lg:col-span-5">
                                <Shield className="w-10 h-10 text-meraki-gold mb-6" />
                                <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">{h.governor_label}</div>
                                <h3 className="font-display text-4xl md:text-5xl uppercase text-meraki-text mb-6">
                                    {h.governor_title} <span className="text-meraki-muted text-2xl">{h.governor_subtitle}</span>
                                </h3>
                                <p className="text-meraki-muted leading-relaxed whitespace-pre-line">{h.governor_paragraph}</p>
                            </div>
                            <div className="lg:col-span-7">
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {governorDuties.map((d) => (
                                        <li key={d} className="flex items-start gap-3 text-sm text-meraki-text/85">
                                            <span className="text-meraki-gold mt-1">▸</span>
                                            <span>{d}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* 2. Ambassador */}
                    <div data-testid="ambassador-card" className="lg:col-span-12 bg-meraki-surface border border-meraki-gold/20 p-8 md:p-12 relative overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-5">
                                <Compass className="w-10 h-10 text-meraki-gold mb-6" />
                                <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">{h.ambassador_label}</div>
                                <h3 className="font-display text-4xl md:text-5xl uppercase text-meraki-text mb-6">
                                    {h.ambassador_title}<br /><span className="text-meraki-gold">{h.ambassador_title_2}</span>
                                </h3>
                                <p className="text-meraki-muted leading-relaxed whitespace-pre-line">{h.ambassador_paragraph}</p>
                            </div>
                            <div className="lg:col-span-7">
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {ambassadorDuties.map((d) => (
                                        <li key={d} className="flex items-start gap-3 text-sm text-meraki-text/85">
                                            <span className="text-meraki-gold mt-1">▸</span>
                                            <span>{d}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8 flex flex-wrap gap-6 text-xs uppercase tracking-[0.25em] text-meraki-muted">
                                    <div className="flex items-center gap-2"><Handshake className="w-4 h-4 text-meraki-gold" /> Collab Rides</div>
                                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-meraki-gold" /> Outstation Tours</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Active Riders */}
                    <div className={`${showPositions ? "lg:col-span-6" : "lg:col-span-12"} bg-meraki-surface border border-meraki-gold/20 p-8 md:p-10 relative overflow-hidden`} data-testid="stat-riders">
                        <Users className="w-8 h-8 text-meraki-gold mb-4" />
                        <div className="font-display text-7xl md:text-8xl text-meraki-gold leading-none">{h.riders_count}</div>
                        <div className="text-xs uppercase tracking-[0.3em] text-meraki-muted mt-3">{h.riders_label}</div>
                    </div>

                    {/* 4. Open Roles */}
                    {showPositions && (() => {
                        const url = h.open_positions_url;
                        const inner = (
                            <>
                                <Sparkles className="w-8 h-8 text-meraki-gold mb-4" />
                                <div className="font-display text-7xl md:text-8xl text-meraki-text leading-none">{h.open_positions}</div>
                                <div className="text-xs uppercase tracking-[0.3em] text-meraki-gold mt-3">{h.open_positions_label}</div>
                                <div className="mt-4 inline-flex items-center gap-2 text-sm text-meraki-text/90 group-hover:text-meraki-gold transition-colors">
                                    {h.open_positions_note}
                                </div>
                            </>
                        );
                        const sharedStyle = { backgroundImage: `linear-gradient(rgba(5,5,5,0.85), rgba(5,5,5,0.85)), url(${texture})` };
                        const sharedCls = "group lg:col-span-6 relative border border-meraki-gold p-8 md:p-10 overflow-hidden bg-cover bg-center";
                        return url ? (
                            <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="stat-positions"
                                className={`${sharedCls} hover:bg-meraki-gold/10 transition-colors cursor-pointer block`}
                                style={sharedStyle}
                            >
                                {inner}
                            </a>
                        ) : (
                            <div data-testid="stat-positions" className={sharedCls} style={sharedStyle}>
                                {inner}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </section>
    );
}
